// Development ▸ CAD Library ▸ Illustrator: install the Falcon panel for Adobe Illustrator, link it with a one-time code,
// and see (and unlink) the computers linked. The panel sends drawings straight into the CAD Library (routes/illustrator.ts).
import { confirmBox, del, esc, formatDateTime, get, post, toast } from "../lib.js";
import { onAction } from "../state.js";

interface Link { id: string; device: string; person: string; linkedAt: string; lastUsedAt: string | null; mine: boolean }
interface AiData { links: Link[]; canLink: boolean; admin: boolean; minutes: number }
let data: AiData | null = null;
let code: { code: string; expiresAt: string } | null = null;

export async function loadIllustrator() { data = await get("/api/org/cad/illustrator/links"); return data; }

/** "Sent from Illustrator by Umar Farooq, 16/10/2026 15:12" (library tiles and the designer). */
export const aiSentLine = (at: string, by: string) => `<span class="cad-ai-mark" aria-hidden="true">Ai</span> Sent from Illustrator${by ? ` by ${esc(by)}` : ""}, ${esc(formatDateTime(at))}`;

export function illustratorTab(): string {
  const d = data || { links: [], canLink: false, admin: false, minutes: 10 };
  const rows = d.links.map((l) => `<tr><td>${esc(l.device)}</td>${d.admin ? `<td>${esc(l.person)}</td>` : ""}<td>${esc(formatDateTime(l.linkedAt))}</td><td>${l.lastUsedAt ? esc(formatDateTime(l.lastUsedAt)) : "–"}</td><td><button type="button" class="link-btn danger-link" data-action="cad-ai-unlink" data-id="${esc(l.id)}" data-device="${esc(l.device)}">Unlink</button></td></tr>`).join("");
  return `<div class="cad-ai" id="cad-ai">
<div class="cad-ai-intro"><h3>Use Illustrator with Falcon</h3>
<p class="muted">The Falcon panel inside Adobe Illustrator sends your drawing straight into a CAD Library design, with no saving or importing. It also brings Falcon's colours (with Pantone) and trims into Illustrator. It's free and works with your usual Illustrator subscription, on Windows and Mac.</p></div>
${d.canLink ? `<ol class="cad-ai-steps">
<li><strong>Download the panel</strong><span class="muted">One .zip file with the panel and two install files.</span><a class="secondary" href="/api/org/cad/illustrator/panel.zip" download>Download the Falcon panel</a></li>
<li><strong>Install it once</strong><span class="muted">Close Illustrator. Open the downloaded folder and double-click <b>Install on Windows</b> or <b>Install on Mac</b>. Open Illustrator and choose <b>Window → Extensions → Falcon PLM</b>.</span></li>
<li><strong>Link it</strong><span class="muted">Make a code here and type it in the panel. The code works once, for ${d.minutes} minutes.</span>
${code ? `<span class="cad-ai-code" aria-label="Link code">${esc(code.code)}</span><small class="muted">Works until ${esc(formatDateTime(code.expiresAt))}.</small>` : ""}
<button type="button" class="primary" data-action="cad-ai-code">${code ? "Make a new code" : "Make a link code"}</button></li>
</ol>` : `<p class="muted">To link Illustrator you need <b>Full</b> access to Development → CAD Library. Ask an Admin.</p>`}
<h4 class="cad-subhead">Linked computers</h4>
${rows ? `<div class="cad-parts-wrap"><table class="falcon-table cad-ai-table"><thead><tr><th>Computer</th>${d.admin ? "<th>Person</th>" : ""}<th>Linked</th><th>Last used</th><th><span class="sr-only">Unlink</span></th></tr></thead><tbody>${rows}</tbody></table></div>`
    : `<p class="muted">No computers linked yet.</p>`}
<p class="muted cad-ai-note">The panel works as you, with your own CAD Library access. Unlink a computer you no longer use; it stops working at once.</p>
</div>`;
}

function repaint() { const el = document.getElementById("cad-ai"); if (el) el.outerHTML = illustratorTab(); }

onAction("cad-ai-code", async () => {
  try {
    code = await post("/api/org/cad/illustrator/code", {});
    repaint();
  } catch (err) { toast((err as Error).message); }
});
onAction("cad-ai-unlink", async (el) => {
  if (!(await confirmBox(`Unlink Illustrator on ${el.dataset.device}? The panel there stops working until it is linked again.`, "Unlink", true))) return;
  try {
    await del(`/api/org/cad/illustrator/links/${el.dataset.id}`);
    await loadIllustrator();
    repaint();
    toast("Unlinked.");
  } catch (err) { toast((err as Error).message); }
});
